
import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { translations } from '../translations';
import type { Locale, LanguageContextType } from '../types/language';

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);
const supportedLocales = ['en', 'de', 'he'] as const;

const getLocaleFromPathname = (pathname: string): Locale => {
  const firstSegment = pathname.split('/').filter(Boolean)[0];
  return supportedLocales.includes(firstSegment as Locale) ? (firstSegment as Locale) : 'de';
};

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const [locale, setLocale] = useState<Locale>(() => getLocaleFromPathname(location.pathname));
  const navigate = useNavigate();

  useEffect(() => {
    const pathLocale = getLocaleFromPathname(location.pathname);
    if (pathLocale !== locale) {
      setLocale(pathLocale);
    }
  }, [location.pathname, locale]);

  const handleSetLocale = (newLocale: Locale) => {
    setLocale(newLocale);
    
    // Navigate to the new locale URL
    const currentPath = window.location.pathname;
    const currentHash = window.location.hash;
    
    // Remove the current locale from the path if it exists
    const pathWithoutLocale = currentPath.replace(/^\/(en|de|he)(?=\/|$)/, '') || '/';

    // Navigate to the new locale path
    const localizedPath = newLocale === 'de' && pathWithoutLocale === '/'
      ? '/'
      : `/${newLocale}${pathWithoutLocale}`;
    navigate(`${localizedPath}${currentHash}`);
  };

  const t = (key: string) => {
    return translations[locale]?.[key as keyof typeof translations[typeof locale]] || key;
  };

  const getLocalizedPath = (path: string) => {
    if (locale === 'de' && (path === '/' || path.startsWith('/#'))) {
      return path;
    }

    return `/${locale}${path}`;
  };

  return (
    <LanguageContext.Provider value={{ t, locale, setLocale: handleSetLocale, getLocalizedPath }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    console.error('useLanguage must be used within a LanguageProvider');
    // Return a default context to prevent crashes
    return {
      t: (key: string) => key,
      locale: 'de' as Locale,
      setLocale: () => {},
      getLocalizedPath: (path: string) => path
    };
  }
  return context;
};

export { type Locale };
